import { Component, inject, Input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
	DividerComponent,
	IconComponent,
	InfoGroupComponent,
	TagComponent,
	TagIconComponent,
} from '@tecalisdev/infinity';
import { LanguageService } from '../../../core/services/language.service';
import { MasterService } from '../../../shared/services/master.service';
import { ITextLocaleMap } from '../../models/operations.models';
import {
	formatFormOperationFieldDisplayValue,
	isFormOperationSelectListField,
	resolveFormOperationFieldOptionLabels,
} from '../../utils/form-operation-detail-display.util';
import { resolveFormOperationFieldLabel } from '../../utils/form-operation-detail-field-labels.util';
import {
	FormOperationDetailSectionId,
	IFormOperationDetailSectionView,
} from '../../utils/form-operation-detail-sections.util';
import { resolveWorkflowLocaleText } from '../../../workflows/utils/workflow-locale-text.util';

/**
 * Bloque «Datos» del detalle FORM (Figma `9260-133626`): secciones semánticas + info-groups.
 * Compartido entre detalle de operación y detalle de proceso.
 */
@Component({
	selector: 'app-form-operation-detail-sections',
	imports: [
		TranslatePipe,
		InfoGroupComponent,
		DividerComponent,
		IconComponent,
		TagComponent,
		TagIconComponent,
	],
	templateUrl: './form-operation-detail-sections.component.html',
	styleUrl: './form-operation-detail-sections.component.scss',
})
export class FormOperationDetailSectionsComponent {
	@Input() sections: IFormOperationDetailSectionView[] = [];
	/** `activity`: selects única/múltiple a ancho completo y en lista (modal actividad). */
	@Input() variant: 'default' | 'activity' = 'default';

	private languageService = inject(LanguageService);
	private masterService = inject(MasterService);

	public isSelectListField(field: IFormOperationDetailSectionView['fields'][number]): boolean {
		return isFormOperationSelectListField(field);
	}

	public resolveFieldOptionLabels(field: IFormOperationDetailSectionView['fields'][number]): string[] {
		this.languageService.currentLang();
		return resolveFormOperationFieldOptionLabels(field, this.buildDisplayDeps());
	}

	private buildDisplayDeps() {
		return {
			translate: (key: string) => this.masterService.trans.instant(key),
			hasTranslation: (key: string) => this.masterService.hasTranslation(key),
			locale: this.languageService.getLanguage(),
		};
	}

	public resolveLocaleText(value: string | ITextLocaleMap | null | undefined): string {
		this.languageService.currentLang();
		return resolveWorkflowLocaleText(value, this.languageService.getLanguage());
	}

	public resolveFieldValue(field: IFormOperationDetailSectionView['fields'][number]): string {
		this.languageService.currentLang();
		return formatFormOperationFieldDisplayValue(field, this.buildDisplayDeps());
	}

	public resolveFieldLabel(
		field: IFormOperationDetailSectionView['fields'][number],
		sectionId: FormOperationDetailSectionId,
	): string {
		this.languageService.currentLang();
		return resolveFormOperationFieldLabel(field, sectionId, {
			locale: this.languageService.getLanguage(),
			translate: (key) => this.masterService.trans.instant(key),
			hasTranslation: (key) => this.masterService.hasTranslation(key),
		});
	}
}
